/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Button,
  Modal,
} from 'antd';
import Table from 'components/Table';
import { getEstadosGlobales } from 'api/catalogos';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import NumericInput from 'components/NumericInput';
import PercentageInput from 'components/PercentageInput';
import ModalDelete from 'components/ModalDelete';
import percentFormatter from 'utils/percentFormatter';
import { onError } from 'utils/handlers';
import { formatReceivedValues } from 'utils/formatValues';
import {
  getVacacionesRangosDias,
  getVacacionesRangoDia,
  postVacacionesRangoDia,
  patchVacacionesRangoDia,
  deleteVacacionesRangoDia,
} from 'api/nominas/vacaciones/vacacionesRangosDeDias';
import { FLOAT_NUMER_PATTERN, integerRegExp } from 'utils/patterns';

export const vacacionesConfiguracionTablePermission = {
  permissionModel: 'vacacionrangodedia',
  catalogsModels: [],
  tabsModels: [],
};

/* eslint-disable react/prop-types */
const VacacionesConfiguracionTable = ({
  permission,
  selectedTipoNominaEmpleado,
  selectedRowParent,
  tiposNominasEmpleados,
  estatusAutorizado,
  estatusInactivo,
}) => {
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [estados, setEstados] = useState();
  const [data, setData] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getVacacionesRangosDias({ vacacion_tipo_de_nomina_empleado: selectedRowParent });
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getVacacionesRangosDias({ vacacion_tipo_de_nomina_empleado: selectedRowParent }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickAdd = () => {
    // Limpieza directa sin cerrar/abrir bruscamente
    setSelectedRowKeys([]);
    form.resetFields();

    // Abrir el modal inmediatamente
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onClickEdit = async (responseData = null) => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const targetId = responseData?.id || key;
      const match = responseData || data.find((item) => item.id === targetId);

      if (!match) return;

      const _vacacionesRangoDias = await getVacacionesRangoDia(match.id);

      if (_vacacionesRangoDias) {
        form.resetFields();
        form.setFieldsValue(formatReceivedClone(_vacacionesRangoDias));
        form.setFieldValue('vacacion_tipo_de_nomina_empleado', selectedTipoNominaEmpleado);
        setVisible(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calcTotalDays = () => {
    const numeroDePeriodos = form.getFieldValue('numero_de_periodos');
    const diasPorPeriodo = form.getFieldValue('numero_de_dias_habiles_por_periodo');

    // eslint-disable-next-line no-restricted-globals
    if ((!isNaN(numeroDePeriodos) && numeroDePeriodos) && (!isNaN(diasPorPeriodo) && diasPorPeriodo)) {
      const totalDiasPorAnio = parseInt(numeroDePeriodos, 10) * parseInt(diasPorPeriodo, 10);
      form.setFieldsValue({ total_de_dias_habiles_por_ano: totalDiasPorAnio });
      form.validateFields(['total_de_dias_habiles_por_ano']);
    }
  };

  const onFinish = async (_values, _continue = true) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        vacacion_tipo_de_nomina_empleado: selectedRowParent,
      };
      const [key] = selectedRowKeys;

      const saved = await (key ? patchVacacionesRangoDia(key, values) : postVacacionesRangoDia(values));
      if (saved) {
        await fetchData();
        if (_continue) {
          // Si continúa, solo actualiza la selección en lugar de cerrar y abrir
          setSelectedRowKeys([saved.id]);
        } else {
          onCancel();
        }
      }
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteVacacionesRangoDia(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
    integer: [
      requiredRule,
      {
        pattern: integerRegExp,
        message: 'Ingrese un valor válido',
      },
    ],
    float: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
    porcentaje: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
      {
        validator: async (rule, value) => {
          if (value > 100) {
            throw new Error('El porcentaje no puede ser mayor a 100%');
          }
        },
      },
    ],
    total_dias: [
      requiredRule,
      {
        pattern: integerRegExp,
        message: 'Ingrese un valor válido',
      },
      {
        validator: async (rule, value) => {
          if (parseInt(value, 10) > 365) {
            throw new Error('El total de días por año no puede ser mayor a 365');
          }
        },
      },

    ],
  };

  const columns = [
    {
      titleText: 'Desde Años',
      dataIndex: 'de_anos',
      key: 'de_anos',
      width: 200,
    },
    {
      titleText: 'Desde No. de Días',
      dataIndex: 'de_numero_dias',
      key: 'de_numero_dias',
      width: 200,
    },
    {
      titleText: 'Hasta Años',
      dataIndex: 'hasta_anos',
      key: 'hasta_anos',
      width: 200,
    },
    {
      titleText: 'Hasta No. de Días',
      dataIndex: 'hasta_numero_dias',
      key: 'hasta_numero_dias',
      width: 200,
    },
    {
      titleText: 'No. de Periodos',
      dataIndex: 'numero_de_periodos',
      key: 'numero_de_periodos',
      width: 300,
    },
    {
      titleText: 'No. de Días Hábiles por Periodo',
      dataIndex: 'numero_de_dias_habiles_por_periodo',
      key: 'numero_de_dias_habiles_por_periodo',
      width: 300,
    },
    {
      titleText: 'Total de Días Hábiles por Año',
      dataIndex: 'total_de_dias_habiles_por_ano',
      key: 'total_de_dias_habiles_por_ano',
      width: 300,
    },
    {
      titleText: '% de Prima Vacacional',
      dataIndex: 'porcentaje_prima_vacacional',
      key: 'porcentaje_prima_vacacional',
      width: 300,
      render: (val) => percentFormatter(val),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          data={data}
          cols={columns}
          allowExportParam={false}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableAdd={estatusAutorizado || estatusInactivo}
          disableDelete={estatusAutorizado || estatusInactivo}
          permission={permission}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Configuración de Vacaciones`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          width={750}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="vacacion_tipo_de_nomina_empleado"
                  label="Tipo de Nómina Empleado"
                  rules={rules.required}
                  hasFeedback
                  initialValue={selectedTipoNominaEmpleado}
                >
                  <Select
                    dataSource={tiposNominasEmpleados}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="de_anos"
                  label="Desde Años"
                  rules={rules.integer}
                  disabled={estatusAutorizado || estatusInactivo}
                  hasFeedback
                />
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="de_numero_dias"
                  label="Desde No. de Días"
                  rules={rules.integer}
                  disabled={estatusAutorizado || estatusInactivo}
                  hasFeedback
                />
              </Col>
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="hasta_anos"
                  label="Hasta Años"
                  rules={rules.integer}
                  disabled={estatusAutorizado || estatusInactivo}
                  hasFeedback
                />
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="hasta_numero_dias"
                  label="Hasta No. de Días"
                  rules={rules.integer}
                  disabled={estatusAutorizado || estatusInactivo}
                  hasFeedback
                />
              </Col>
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="numero_de_periodos"
                  label="Periodos"
                  rules={rules.integer}
                  onChange={calcTotalDays}
                  disabled={estatusAutorizado || estatusInactivo}
                  hasFeedback
                />
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="numero_de_dias_habiles_por_periodo"
                  label="No. de Días por Periodo"
                  rules={rules.integer}
                  onChange={calcTotalDays}
                  disabled={estatusAutorizado || estatusInactivo}
                  hasFeedback
                />
              </Col>
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="total_de_dias_habiles_por_ano"
                  label="Total de Días por Año"
                  rules={rules.total_dias}
                  disabled
                  hasFeedback
                />
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <PercentageInput
                  name="porcentaje_prima_vacacional"
                  label="% de Prima Vacacional"
                  rules={rules.porcentaje}
                  disabled={estatusAutorizado || estatusInactivo}
                  hasFeedback
                />
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.estatus}
                  hasFeedback
                  initialValue={1}
                >
                  <Select
                    dataSource={estados}
                    disabled={!selectedRowKeys?.length || estatusAutorizado || estatusInactivo}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="la Configuración de Vacaciones"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default VacacionesConfiguracionTable;
